import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ShieldCheck,
  Store,
  Users,
  CreditCard,
  Check,
  X,
  Plus,
  Pencil,
  Trash2,
  MapPin,
  Loader2,
  Star,
} from "lucide-react";
import DashboardShell from "@/components/DashboardShell";
import api, { apiErr } from "@/lib/api";
import { normalizeShop } from "@/lib/adapt";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const NAV = [
  { key: "shops", label: "Manage Shops", icon: Store },
  { key: "approvals", label: "Approvals", icon: ShieldCheck },
  { key: "users", label: "Users", icon: Users },
  { key: "payments", label: "Payments", icon: CreditCard },
];

const asList = (d, key) => (Array.isArray(d) ? d : d?.[key] || []);
const EMPTY_FORM = {
  name: "",
  category: "",
  description: "",
  address: "",
  city: "",
  state: "",
  lat: "",
  lng: "",
  phone: "",
};

export default function AdminPanel() {
  const [active, setActive] = useState("shops");
  const [stats, setStats] = useState(null);
  const [shops, setShops] = useState([]);
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // shop editor
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [shopRes, uRes, payRes, catRes] = await Promise.all([
        api.get("/admin/shops").catch(() => ({ data: [] })),
        api.get("/admin/users").catch(() => ({ data: [] })),
        api.get("/admin/payments").catch(() => ({ data: [] })),
        api.get("/categories").catch(() => ({ data: {} })),
      ]);
      const allShops = asList(shopRes.data, "shops").map(normalizeShop);
      const userList = asList(uRes.data, "users");
      const payList = asList(payRes.data, "payments");
      setShops(allShops);
      setPending(allShops.filter((s) => s.approval_status !== "approved"));
      setUsers(userList);
      setPayments(payList);
      setCategories(catRes.data?.categories || []);
      setStats({
        users: userList.length,
        shops: allShops.length,
        pending: allShops.filter((s) => s.approval_status !== "approved").length,
        revenue: payList.reduce((sum, p) => sum + (p.amount || p.value || 0), 0),
      });
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (shop) => {
    const r = shop._raw || {};
    setEditingId(shop.id);
    setForm({
      name: r.name || "",
      category: r.category || "",
      description: r.description || "",
      address: r.address || "",
      city: r.city || "",
      state: r.state || "",
      lat: r.lat ?? "",
      lng: r.lng ?? "",
      phone: r.phone || "",
    });
    setDialogOpen(true);
  };

  const geocode = async () => {
    const q = [form.address, form.city, form.state].filter(Boolean).join(", ");
    if (!q) return toast.error("Enter an address/city first");
    setGeocoding(true);
    try {
      const { data } = await api.get("/geocode/search", { params: { q } });
      const first = data?.results?.[0];
      if (first) {
        setForm((f) => ({ ...f, lat: first.lat, lng: first.lng }));
        toast.success("Coordinates filled from address");
      } else {
        toast.error("No coordinates found for that address");
      }
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setGeocoding(false);
    }
  };

  const save = async () => {
    if (!form.name || !form.category || !form.address) {
      return toast.error("Name, category and address are required");
    }
    if (form.lat === "" || form.lng === "") {
      return toast.error("Set coordinates (use 'Get coordinates' or enter manually)");
    }
    const payload = {
      name: form.name,
      category: form.category,
      description: form.description,
      address: form.address,
      city: form.city,
      state: form.state,
      phone: form.phone,
      lat: Number(form.lat),
      lng: Number(form.lng),
    };
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/shops/${editingId}`, payload);
        toast.success("Shop updated");
      } else {
        await api.post("/shops", payload);
        toast.success("Shop created");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/shops/${deleteTarget.id}`);
      toast.success("Shop deleted");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
      setDeleteTarget(null);
    }
  };

  const decide = async (id, approve) => {
    try {
      await api.post(`/admin/shops/${id}/verify`, { approve, verified: approve });
      toast.success(approve ? "Shop approved" : "Shop rejected");
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail));
    }
  };

  return (
    <DashboardShell
      title="Admin panel"
      subtitle="Manage shops, approvals, users and payments."
      nav={NAV}
      active={active}
      onNav={setActive}
    >
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat label="Total shops" value={stats.shops} icon={Store} />
          <Stat label="Pending" value={stats.pending} icon={ShieldCheck} />
          <Stat label="Users" value={stats.users} icon={Users} />
          <Stat label="Revenue" value={`₹${stats.revenue}`} icon={CreditCard} />
        </div>
      )}

      {/* ===== MANAGE SHOPS ===== */}
      {active === "shops" && (
        <div data-testid="admin-manage-shops">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display font-bold text-xl">All shops ({shops.length})</h3>
            <button
              data-testid="admin-add-shop-btn"
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-5 py-2.5 hover:bg-primary/90 transition-colors"
            >
              <Plus className="w-4 h-4" /> Add shop
            </button>
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-56 rounded-3xl bg-secondary animate-pulse" />
              ))}
            </div>
          ) : shops.length === 0 ? (
            <p className="text-muted-foreground">No shops yet. Click “Add shop” to create one.</p>
          ) : (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {shops.map((s) => (
                <div key={s.id} className="bg-card rounded-3xl border border-border overflow-hidden flex flex-col">
                  <div className="h-32 overflow-hidden">
                    <img src={s.images?.[0]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-display font-bold text-lg leading-tight">{s.name}</h4>
                      <span
                        className={`text-xs font-bold px-2.5 py-1 rounded-full shrink-0 ${
                          s.approval_status === "approved"
                            ? "bg-success/15 text-success"
                            : "bg-primary/10 text-primary"
                        }`}
                      >
                        {s.approval_status === "approved" ? "live" : "pending"}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground capitalize mt-1">{s.category}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" /> {s.address || "No address"}
                    </p>
                    <div className="flex items-center gap-1 mt-2 text-sm">
                      <Star className="w-4 h-4 fill-primary text-primary" /> {s.rating} ({s.review_count})
                    </div>
                    <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                      <button
                        data-testid={`admin-edit-shop-${s.id}`}
                        onClick={() => openEdit(s)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-border font-semibold py-2 text-sm hover:border-primary hover:text-primary transition-colors"
                      >
                        <Pencil className="w-4 h-4" /> Edit
                      </button>
                      <button
                        data-testid={`admin-delete-shop-${s.id}`}
                        onClick={() => setDeleteTarget(s)}
                        className="inline-flex items-center justify-center gap-1.5 rounded-full border border-border font-semibold py-2 px-4 text-sm text-destructive hover:border-destructive transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== APPROVALS ===== */}
      {active === "approvals" && (
        <div data-testid="admin-approvals">
          <h3 className="font-display font-bold text-xl mb-5">Pending shop approvals</h3>
          {pending.length === 0 ? (
            <p className="text-muted-foreground">Nothing pending. All caught up.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {pending.map((s) => (
                <div key={s.id} className="bg-card rounded-3xl border border-border overflow-hidden">
                  <div className="h-36 overflow-hidden">
                    <img src={s.images?.[0]} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5">
                    <h4 className="font-display font-bold text-lg">{s.name}</h4>
                    <p className="text-sm text-muted-foreground capitalize">
                      {s.category} · {s.address}
                    </p>
                    <p className="text-sm mt-2 text-foreground/80">{s.description}</p>
                    <div className="flex gap-3 mt-4">
                      <button
                        data-testid={`approve-${s.id}`}
                        onClick={() => decide(s.id, true)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-success text-white font-semibold py-2.5 hover:opacity-90 transition-opacity"
                      >
                        <Check className="w-4 h-4" /> Approve
                      </button>
                      <button
                        data-testid={`reject-${s.id}`}
                        onClick={() => decide(s.id, false)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-border font-semibold py-2.5 hover:border-destructive hover:text-destructive transition-colors"
                      >
                        <X className="w-4 h-4" /> Reject
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== USERS ===== */}
      {active === "users" && (
        <div className="bg-card rounded-3xl border border-border overflow-hidden" data-testid="admin-users">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr><Th>Name</Th><Th>Email</Th><Th>Role</Th></tr>
            </thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.user_id || u.id || u.email || i} className="border-t border-border">
                  <Td className="font-semibold">{u.name}</Td>
                  <Td className="text-muted-foreground">{u.email}</Td>
                  <Td>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-primary/10 text-primary capitalize">
                      {u.role?.replace("_", " ")}
                    </span>
                  </Td>
                </tr>
              ))}
              {users.length === 0 && <tr><Td className="text-muted-foreground py-8">No users to show.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== PAYMENTS ===== */}
      {active === "payments" && (
        <div className="bg-card rounded-3xl border border-border overflow-hidden" data-testid="admin-payments">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left">
              <tr><Th>Owner</Th><Th>Plan</Th><Th>Amount</Th><Th>Status</Th><Th>Date</Th></tr>
            </thead>
            <tbody>
              {payments.map((p, i) => (
                <tr key={p.payment_id || p.id || i} className="border-t border-border">
                  <Td className="font-semibold">{p.owner_name || p.user_name || p.email || "—"}</Td>
                  <Td>{p.plan || p.plan_name || "—"}</Td>
                  <Td className="font-bold">₹{p.amount ?? p.value ?? 0}</Td>
                  <Td>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${(p.status === "paid" || p.status === "success") ? "bg-success/15 text-success" : "bg-primary/10 text-primary"}`}>
                      {p.status || "—"}
                    </span>
                  </Td>
                  <Td className="text-muted-foreground">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</Td>
                </tr>
              ))}
              {payments.length === 0 && <tr><Td className="text-muted-foreground py-8">No payments to show.</Td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ===== SHOP EDITOR DIALOG ===== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editingId ? "Edit shop" : "Add shop"}</DialogTitle>
            <DialogDescription>
              {editingId ? "Update this shop's details." : "Create a new shop listing."} Coordinates are required for it to appear on the map.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Inp label="Shop name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-shop-name" />
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="admin-shop-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Inp label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="admin-shop-address" />
            <div className="grid grid-cols-2 gap-3">
              <Inp label="City" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="admin-shop-city" />
              <Inp label="State" value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="admin-shop-state" />
            </div>
            <div className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <Inp label="Latitude" value={form.lat} onChange={(v) => setForm({ ...form, lat: v })} testid="admin-shop-lat" />
              <Inp label="Longitude" value={form.lng} onChange={(v) => setForm({ ...form, lng: v })} testid="admin-shop-lng" />
              <button
                type="button"
                data-testid="admin-shop-geocode"
                onClick={geocode}
                disabled={geocoding}
                className="rounded-2xl bg-secondary border border-border px-3 py-3 text-sm font-semibold hover:border-primary transition-colors disabled:opacity-60 flex items-center gap-1.5"
              >
                {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                Get
              </button>
            </div>
            <Inp label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="admin-shop-phone" />
            <Inp label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="admin-shop-desc" />
          </div>
          <DialogFooter>
            <button
              data-testid="admin-shop-save"
              onClick={save}
              disabled={saving}
              className="w-full rounded-full bg-primary text-primary-foreground font-semibold py-3 hover:bg-primary/90 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Save changes" : "Create shop"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DELETE CONFIRM ===== */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the shop from the marketplace. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="admin-delete-confirm"
              onClick={doDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete shop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardShell>
  );
}

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="bg-card rounded-3xl border border-border p-6">
      <span className="grid place-items-center w-11 h-11 rounded-2xl bg-primary/10 text-primary mb-4">
        <Icon className="w-5 h-5" />
      </span>
      <p className="font-display font-extrabold text-3xl tracking-tight">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}
function Label({ children }) {
  return <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1.5">{children}</label>;
}
function Inp({ label, value, onChange, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-2xl bg-secondary border border-border px-4 py-3 font-medium focus:outline-none focus:border-primary transition-colors"
      />
    </div>
  );
}
function Th({ children }) {
  return <th className="px-5 py-4 font-bold text-muted-foreground uppercase text-xs tracking-wider">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-5 py-4 ${className}`}>{children}</td>;
}
